/**
 * Authentication Context
 * Manages authentication state across the application
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import { clearListState } from '../hooks/useListState';
import { authService, type UserResponse, type LoginResponse } from '../services/auth';
import {
  clearTokens,
  downgradeToBearer,
  isCookieMode,
  purgeLegacyTokens,
  startSession,
} from '../services/api';

/**
 * Marks that a session was established, so a logged-out visitor does not
 * trigger a pointless /auth/me on every page load.
 *
 * It is a hint, never a credential: it authenticates nothing, and forging it
 * only earns a 401 from the server.
 */
const SESSION_HINT_KEY = 'bnft-session';

/**
 * Last known user profile, so the app can still render offline when even the
 * Service Worker cache has nothing for /auth/me. The user's own profile, on
 * their own device.
 */
const CACHED_USER_KEY = 'bnft-user';

function setSessionHint(): void {
  try {
    localStorage.setItem(SESSION_HINT_KEY, '1');
  } catch {
    // Non-fatal: costs one extra /auth/me on the next load.
  }
}

function clearSessionHint(): void {
  try {
    localStorage.removeItem(SESSION_HINT_KEY);
    localStorage.removeItem(CACHED_USER_KEY);
  } catch {
    // Non-fatal.
  }
}

function hasSessionHint(): boolean {
  try {
    return localStorage.getItem(SESSION_HINT_KEY) === '1';
  } catch {
    // Storage unavailable: assume a session and let the server decide.
    return true;
  }
}

function cacheUser(user: UserResponse): void {
  try {
    localStorage.setItem(CACHED_USER_KEY, JSON.stringify(user));
  } catch {
    // Non-fatal.
  }
}

function readCachedUser(): UserResponse | null {
  try {
    const raw = localStorage.getItem(CACHED_USER_KEY);
    return raw ? (JSON.parse(raw) as UserResponse) : null;
  } catch {
    return null;
  }
}

/**
 * True only for an actual HTTP 401 — never for a network failure.
 *
 * The distinction is the whole point: a 401 means the server rejected the
 * session, a network error means we could not ask. Treating the second as the
 * first is what would eject an offline field agent to the login screen.
 */
function isUnauthorizedError(error: unknown): boolean {
  return (error as { response?: { status?: number } })?.response?.status === 401;
}

interface AuthState {
  user: UserResponse | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  mustChangePassword: boolean;
}

interface AuthContextType extends AuthState {
  login: (username: string, password: string) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  clearMustChangePassword: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    mustChangePassword: false,
  });

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      // Any JWT left over from before the migration is dead weight in cookie
      // mode — and a liability. Remove it before anything else.
      if (isCookieMode()) {
        purgeLegacyTokens();
      }

      // No local trace of a session: nothing to verify, and asking the server
      // would only produce a pointless 401 on every visit by a logged-out user.
      if (!hasSessionHint()) {
        setState({ user: null, isAuthenticated: false, isLoading: false, mustChangePassword: false });
        return;
      }

      // Note there is NO "do we hold an access token?" pre-check here any more.
      // In cookie mode the credential is HttpOnly and invisible to JavaScript,
      // so such a test always failed and would have logged every user out on
      // every page load. The server is the only authority: ask it.
      try {
        const user = await authService.getCurrentUser();
        cacheUser(user);
        setState({ user, isAuthenticated: true, isLoading: false, mustChangePassword: false });
      } catch (error) {
        // A 401 is a real verdict: the session is gone.
        // A network failure is NOT. A checkpoint agent reloading the app with
        // no connectivity must stay logged in — being ejected to /login in the
        // field, with pending offline work, is the worst outcome there is.
        // (The Service Worker usually serves /auth/me from its cache, so this
        // path is the fallback when even the cache has nothing.)
        if (isUnauthorizedError(error)) {
          clearSessionHint();
          clearTokens();
          setState({ user: null, isAuthenticated: false, isLoading: false, mustChangePassword: false });
          return;
        }

        setState({
          user: readCachedUser(),
          isAuthenticated: true,
          isLoading: false,
          mustChangePassword: false,
        });
      }
    };

    checkAuth();
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const response = await authService.login({ username, password });

    // The server decides the scheme: a csrf_token in the payload means it
    // issued HttpOnly cookies, and no JWT is written to storage.
    const mode = startSession(response);
    setSessionHint();

    // This call doubles as a probe. In cookie mode it is the first request that
    // actually relies on the cookie, so a 401 here means the browser accepted
    // the login but is not storing or sending the cookie — a Secure cookie over
    // plain HTTP, or a privacy mode blocking site data. Rather than leave the
    // user staring at a login screen that "works" but never lets them in, fall
    // back to the legacy bearer scheme: /auth/login still returns the JWTs.
    let user;
    try {
      user = await authService.getCurrentUser();
    } catch (error) {
      if (mode === 'cookie' && isUnauthorizedError(error)) {
        downgradeToBearer(response.access_token, response.refresh_token);
        user = await authService.getCurrentUser();
      } else {
        clearSessionHint();
        throw error;
      }
    }

    cacheUser(user);
    // A new session starts from clean lists: never restore the page/filters
    // of whoever used this tab before.
    clearListState();
    setState({
      user,
      isAuthenticated: true,
      isLoading: false,
      mustChangePassword: response.must_change_password,
    });

    return response;
  }, []);

  const logout = useCallback(async () => {
    try {
      // Must reach the server: only it can expire an HttpOnly cookie. Clearing
      // localStorage alone would leave the session cookie alive in the browser,
      // and the "logged out" user would still be authenticated on their next
      // request.
      await authService.logout();
    } finally {
      clearSessionHint();
      clearTokens();
      clearListState();
      setState({ user: null, isAuthenticated: false, isLoading: false, mustChangePassword: false });
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const user = await authService.getCurrentUser();
      cacheUser(user);
      setState((prev) => ({ ...prev, user }));
    } catch (error) {
      // Same rule as on mount: only a 401 ends the session. A network failure
      // must leave the user where they are — losing the session here would
      // discard whatever they were in the middle of.
      if (!isUnauthorizedError(error)) {
        return;
      }
      clearSessionHint();
      clearTokens();
      setState({ user: null, isAuthenticated: false, isLoading: false, mustChangePassword: false });
    }
  }, []);

  const clearMustChangePassword = useCallback(() => {
    setState((prev) => ({ ...prev, mustChangePassword: false }));
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        refreshUser,
        clearMustChangePassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export { AuthContext };
